import { axiosRestApi } from 'config/axiosRestApi';
import { firebaseAuth } from 'config/firebase';
import {
  ConfirmationResult,
  RecaptchaVerifier,
  signInWithPhoneNumber,
} from 'firebase/auth';
import { useSession } from 'providers';
import { useCallback, useState } from 'react';

const validatePhoneIdTokenRequest = async (idToken: string) => {
  return await axiosRestApi.post<{
    expirationDate: number;
    firebaseId: string;
  }>('/validate-phone-id-token', { idToken });
};

const validateNewPhoneRequest = async (
  idToken: string,
  phone: string,
  countryCallingCode: string,
) => {
  return await axiosRestApi.post<{
    expirationDate: number;
    firebaseId: string;
  }>('/validate-new-phone', { idToken, phone, countryCallingCode });
};

/**
 * @param {RecaptchaVerifier | undefined} recaptchaVerifier - A recaptcha verifier required for calls to Firebase.
 * Please use `useRecaptchaVerifier` to create the verifier, as it will handle proper initialization and
 * removal of the verifier.
 */
export const useTwoFactorAuthFunctions = ({
  recaptchaVerifier,
}: {
  recaptchaVerifier?: RecaptchaVerifier;
}) => {
  const [confirmationResult, setConfirmationResult] = useState<
    ConfirmationResult | undefined
  >(undefined);

  const { updateFirebaseId, updateExpiresIn } = useSession();

  const sendVerificationCodeToUser = useCallback(
    async (phoneWithCallingCode: string) => {
      if (!recaptchaVerifier) {
        throw new Error('Missing recaptcha verifier');
      }

      const signInConfirmationResult = await signInWithPhoneNumber(
        firebaseAuth,
        phoneWithCallingCode,
        recaptchaVerifier,
      );

      setConfirmationResult(signInConfirmationResult);
    },
    [recaptchaVerifier],
  );

  const verifyCode = useCallback(
    async (code: string) => {
      if (!confirmationResult) {
        throw new Error('Must send the verification code to the user first');
      }

      const user = (await confirmationResult.confirm(code)).user;

      return await user.getIdToken();
    },
    [confirmationResult],
  );

  const validatePhoneIdToken = useCallback(
    async (code: string) => {
      const idToken = await verifyCode(code);

      await validatePhoneIdTokenRequest(idToken).then(({ data }) => {
        updateExpiresIn(data.expirationDate.toString());
        updateFirebaseId(data.firebaseId);
      });
    },
    [updateExpiresIn, updateFirebaseId, verifyCode],
  );

  const validateNewPhone = useCallback(
    async (code: string, phone: string, countryCallingCode: string) => {
      const idToken = await verifyCode(code);

      await validateNewPhoneRequest(idToken, phone, countryCallingCode).then(
        ({ data }) => {
          updateExpiresIn(data.expirationDate.toString());
          updateFirebaseId(data.firebaseId);
        },
      );
    },
    [updateExpiresIn, updateFirebaseId, verifyCode],
  );

  return {
    sendVerificationCodeToUser,
    validatePhoneIdToken,
    validateNewPhone,
  };
};
